/**
 * ERC20 Router Approval Hook
 *
 * Manages ERC20 approvals for swap routers.
 * Handles checking allowance, approving, and tracking transaction state.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAccount, useReadContract, useWriteContract } from 'wagmi'
import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import { Address, erc20Abi } from 'viem'
import { isNativeHypeAddress } from '@/domain/euler/nativeHype'

export interface UseErc20RouterApprovalParams {
  /** Token address to approve */
  tokenAddress?: Address
  /** Router/spender address */
  routerAddress?: Address
  /** Amount to approve */
  amount: bigint
  /** Chain ID */
  chainId: number
  /** Whether the hook is enabled */
  enabled?: boolean
}

export type ApprovalStep = 'idle' | 'approving' | 'approved'

export interface UseErc20RouterApprovalResult {
  /** Current approval step */
  step: ApprovalStep
  /** Whether approval is needed */
  needsApproval: boolean
  /** Whether allowance has been checked (not loading) */
  isAllowanceChecked: boolean
  /** Whether approval is loading/pending */
  isLoading: boolean
  /** Approve function */
  approve: () => void
  /** Reset the approval state */
  reset: () => void
  /** Any error that occurred */
  error: Error | null
  /** Refetch allowance */
  refetchAllowance: () => void
}

export function useErc20RouterApproval({
  tokenAddress,
  routerAddress,
  amount,
  chainId,
  enabled = true,
}: UseErc20RouterApprovalParams): UseErc20RouterApprovalResult {
  const { address } = useAccount()
  const [step, setStep] = useState<ApprovalStep>('idle')

  // Check if token is native (doesn't need/support approval)
  const isNative = tokenAddress ? isNativeHypeAddress(tokenAddress) : false

  // Check current allowance (skip for native tokens)
  const {
    data: allowance,
    refetch: refetchAllowance,
    isLoading: isAllowanceLoading,
  } = useReadContract({
    address: tokenAddress,
    abi: erc20Abi,
    functionName: 'allowance',
    args: address && routerAddress ? [address, routerAddress] : undefined,
    chainId,
    query: {
      // Disable for native tokens - they don't support ERC20 allowance
      enabled: enabled && !!tokenAddress && !!routerAddress && !!address && step !== 'approved' && !isNative,
    },
  })

  // Approval transaction
  const {
    writeContract,
    data: approveHash,
    isPending: isApprovePending,
    error: approveError,
    reset: resetWrite,
  } = useWriteContract()

  const { isLoading: isApproveConfirming, isSuccess: isApproveSuccess } = useWaitForTransactionReceiptUniversal({
    hash: approveHash,
    chainId,
  })

  // Determine if approval is needed
  const needsApproval = useMemo(() => {
    if (!enabled || !tokenAddress || !routerAddress || amount === 0n) return false
    // Native tokens don't need approval - they're sent with msg.value
    if (isNative) return false
    if (step === 'approved') return false
    // If allowance hasn't loaded yet, assume we need approval
    if (allowance === undefined) return true
    return allowance < amount
  }, [enabled, tokenAddress, routerAddress, amount, step, allowance, isNative])

  // Update step when approval confirms
  useEffect(() => {
    if (isApproveSuccess && step === 'approving') {
      setStep('approved')
      void refetchAllowance()
    }
  }, [isApproveSuccess, step, refetchAllowance])

  // Reset step if user rejects or error occurs
  useEffect(() => {
    if (approveError && step === 'approving') {
      setStep('idle')
      resetWrite()
    }
  }, [approveError, step, resetWrite])

  // Approve only the exact amount needed for security
  const approve = useCallback(() => {
    if (!address || !tokenAddress || !routerAddress || amount === 0n) return
    setStep('approving')

    writeContract({
      address: tokenAddress,
      abi: erc20Abi,
      functionName: 'approve',
      args: [routerAddress, amount],
      chainId,
    })
  }, [address, tokenAddress, routerAddress, amount, writeContract, chainId])

  // Reset function
  const reset = useCallback(() => {
    setStep('idle')
    resetWrite()
  }, [resetWrite])

  const isLoading = isApprovePending || isApproveConfirming || step === 'approving'
  // For native tokens, allowance is always "checked" (they don't need approval)
  const isAllowanceChecked = isNative || (!isAllowanceLoading && allowance !== undefined)

  return {
    step,
    needsApproval,
    isAllowanceChecked,
    isLoading,
    approve,
    reset,
    error: approveError as Error | null,
    refetchAllowance,
  }
}
